<template>
  <div class="item-log-wrapper">
    <div v-if="!itemLogMeta || !itemLogMeta.context_types || itemLogMeta.context_types.length === 0" class="text-center py-5">
      <b-spinner label="Loading..." variant="primary"></b-spinner>
      <p class="text-muted mt-2">Loading item log options...</p>
    </div>

    <div v-else class="animated fadeIn">
      <b-card no-body class="mb-3 border-0 shadow-sm">
        <b-card-body>
          <b-form @submit.prevent="fetchData">
            <b-row align-v="end">
              <b-col cols="12" md="3" class="mb-2 mb-md-0">
                <label class="small text-muted text-uppercase mb-1">Filter</label>
                <b-form-select v-model="filterType" :options="filterModes" @change="onFilterTypeChanged"></b-form-select>
              </b-col>

              <b-col v-if="filterType === 'ctx_type'" cols="12" md="4" class="mb-2 mb-md-0">
                <label class="small text-muted text-uppercase mb-1">Context Type</label>
                <b-form-select v-model="contextType" :options="contextTypeOptions"></b-form-select>
              </b-col>

              <b-col v-else cols="12" md="4" class="mb-2 mb-md-0">
                <label class="small text-muted text-uppercase mb-1">{{ selectedFilterLabel }}</label>
                <b-form-input
                  v-model.trim="filterValue"
                  :placeholder="filterPlaceholder"
                  @keyup.enter="fetchData"
                ></b-form-input>
              </b-col>

              <b-col cols="6" md="2" class="mb-2 mb-md-0">
                <label class="small text-muted text-uppercase mb-1">Time Range</label>
                <b-form-select v-model="days" :options="dayOptions"></b-form-select>
              </b-col>

              <b-col cols="6" md="1" class="mb-2 mb-md-0">
                <label class="small text-muted text-uppercase mb-1">Limit</label>
                <b-form-select v-model="limit" :options="limitOptions"></b-form-select>
              </b-col>

              <b-col cols="12" md="2" class="text-md-right">
                <b-button type="submit" variant="primary" :disabled="!canSearch || loading" class="px-4">
                  <b-spinner v-if="loading" small class="mr-1"></b-spinner>
                  <i v-else class="fa fa-search mr-1"></i> Search
                </b-button>
              </b-col>
            </b-row>
          </b-form>
        </b-card-body>
      </b-card>

      <b-alert v-if="loading" show variant="info" class="mb-3">
        <b-spinner small class="mr-2"></b-spinner>
        Searching item log. This query times out in {{ searchSecondsRemaining }} seconds.
      </b-alert>

      <b-alert v-if="itemLogError" show variant="danger" class="mb-3">
        <strong>{{ itemLogError.title || 'Item Log' }}:</strong> {{ itemLogError.message || itemLogError }}
      </b-alert>

      <b-alert v-if="!hasSearched && !loading" show variant="secondary" class="mb-3">
        Select filters and click Search to load item log entries.
      </b-alert>

      <b-overlay :show="loading" variant="transparent" opacity="0.65" blur="1px" rounded="sm">
        <template #overlay>
          <div class="text-center">
            <b-spinner variant="primary" class="mb-2"></b-spinner>
            <div class="font-weight-bold text-primary">Searching Item Log...</div>
          </div>
        </template>
        <div class="rounded shadow-sm border" style="min-height: 500px;">
          <ag-grid-vue
            style="width: 100%; height: 700px;"
            class="ag-theme-balham-dark"
            :gridOptions="gridOptions"
            :rowData="records"
            :enableColResize="true"
            :enableSorting="true"
            :enableFilter="true"
            :suppressClickEdit="true"
            :suppressRowClickSelection="true"
          ></ag-grid-vue>
        </div>
      </b-overlay>
    </div>
  </div>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue'
import { mapGetters } from 'vuex'
import moment from 'moment'
import { DefaultGridOptions } from '@/default_table_settings'
import { applyGridPersistence } from '@/grid_persistence'

export default {
  name: 'ItemLog',
  components: {
    'ag-grid-vue': AgGridVue
  },
  props: {
    username: { type: String, required: true },
    server: { type: String, required: true }
  },
  data() {
    return {
      filterType: 'ctx_type',
      contextType: '',
      filterValue: '',
      days: 30,
      limit: 250,
      loading: false,
      hasSearched: false,
      searchSecondsRemaining: 120,
      searchTimer: null,
      gridOptions: null,
      dayOptions: [
        { value: 30, text: '30 Days' },
        { value: 90, text: '90 Days' },
        { value: 365, text: '365 Days' },
        { value: 0, text: 'All' }
      ],
      limitOptions: [
        { value: 100, text: '100' },
        { value: 250, text: '250' },
        { value: 500, text: '500' },
        { value: 1000, text: '1000' }
      ]
    }
  },
  computed: {
    ...mapGetters({
      itemLog: 'itemLog',
      itemLogMeta: 'itemLogMeta',
      itemLogError: 'itemLogError'
    }),
    records() {
      if (!this.itemLog || !this.itemLog.data || !this.itemLog.data.data) return []
      return this.itemLog.data.data
    },
    contextTypeOptions() {
      if (!this.itemLogMeta || !this.itemLogMeta.context_types) return []
      return this.itemLogMeta.context_types.map(option => ({
        value: option.value,
        text: option.label
      }))
    },
    filterModes() {
      const modes = [{ value: 'ctx_type', text: 'Context Type' }]
      if (!this.itemLogMeta || !this.itemLogMeta.filters) return modes
      return modes.concat(this.itemLogMeta.filters.map(option => ({
        value: option.value,
        text: option.label
      })))
    },
    selectedFilterLabel() {
      const selected = this.filterModes.find(option => option.value === this.filterType)
      return selected ? selected.text : 'Value'
    },
    filterPlaceholder() {
      const placeholders = {
        npc_id: 'NPCID, e.g. 1234',
        poke_uid: 'PokeUID, e.g. 987654',
        item_name: 'Item name',
        lootbox_source: 'Lootbox item name',
        opened_lootbox: 'Lootbox item name',
        staff_target: 'Target username'
      }
      return placeholders[this.filterType] || 'Filter value'
    },
    canSearch() {
      if (!this.username || !this.server) return false
      if (this.filterType === 'ctx_type') return this.contextType !== ''
      return this.filterValue !== ''
    }
  },
  watch: {
    username() {
      this.handleScopeChanged()
    },
    server() {
      this.handleScopeChanged()
    },
    itemLog() {
      this.loading = false
      this.stopSearchTimer()
    },
    itemLogError() {
      this.loading = false
      this.stopSearchTimer()
    },
    itemLogMeta: {
      immediate: true,
      handler(meta) {
        if (!meta || !meta.context_types || meta.context_types.length === 0) return
        this.ensureValidSelection()
      }
    }
  },
  beforeMount() {
    this.gridOptions = Object.assign({}, DefaultGridOptions, {
      suppressRowClickSelection: true,
      rowSelection: 'multiple',
      getContextMenuItems: this.getContextMenuItems,
      defaultColDef: {
        resizable: true,
        sortable: true,
        filter: true
      },
      onFirstDataRendered: params => params.api.sizeColumnsToFit()
    })
    applyGridPersistence(this.gridOptions, 'user-item-log-grid')
    this.gridOptions.columnDefs = this.createColumnDefs()
  },
  created() {
    this.$store.commit('setItemLog', { data: { data: [] } })
    this.emit('getItemLogMeta', {})
  },
  beforeDestroy() {
    this.stopSearchTimer()
  },
  methods: {
    ensureValidSelection() {
      if (this.filterType !== 'ctx_type' && !this.filterModes.find(option => option.value === this.filterType)) {
        this.filterType = 'ctx_type'
      }
      if (this.filterType === 'ctx_type' && !this.contextTypeOptions.find(option => option.value === this.contextType)) {
        this.contextType = this.contextTypeOptions.length > 0 ? this.contextTypeOptions[0].value : ''
      }
    },
    onFilterTypeChanged() {
      this.filterValue = ''
      this.ensureValidSelection()
    },
    handleScopeChanged() {
      this.loading = false
      this.hasSearched = false
      this.stopSearchTimer()
      this.$store.commit('setItemLog', { data: { data: [] } })
    },
    fetchData() {
      this.ensureValidSelection()
      if (!this.canSearch || !this.itemLogMeta) return

      this.loading = true
      this.hasSearched = true
      this.startSearchTimer()
      this.$store.commit('setItemLogError', null)
      this.emit('searchItemLog', {
        username: this.username,
        server: this.server,
        filter_type: this.filterType,
        context_type: this.filterType === 'ctx_type' ? this.contextType : '',
        value: this.filterType === 'ctx_type' ? '' : this.filterValue,
        days: this.days,
        limit: this.limit
      })
    },
    startSearchTimer() {
      this.stopSearchTimer()
      this.searchSecondsRemaining = 120
      this.searchTimer = window.setInterval(() => {
        if (this.searchSecondsRemaining > 0) {
          this.searchSecondsRemaining -= 1
        }
      }, 1000)
    },
    stopSearchTimer() {
      if (this.searchTimer) {
        window.clearInterval(this.searchTimer)
        this.searchTimer = null
      }
    },
    renderQty(value) {
      const qty = Number(value || 0)
      const formatted = Math.abs(qty).toLocaleString()
      if (qty < 0) {
        return `<span class="text-danger font-weight-bold">-${formatted}</span>`
      }
      return `<span class="text-success font-weight-bold">+${formatted}</span>`
    },
    createColumnDefs() {
      return [
        {
          headerName: 'ID',
          field: 'id',
          hide: true,
          width: 100,
          filter: 'agNumberColumnFilter'
        },
        {
          headerName: 'Username',
          field: 'username',
          filter: 'agSetColumnFilter',
          width: 150
        },
        {
          headerName: 'Qty',
          field: 'qty',
          filter: 'agNumberColumnFilter',
          filterParams: {
            defaultOption: 'equals'
          },
          width: 110,
          cellRenderer: params => this.renderQty(params.value)
        },
        {
          headerName: 'Item',
          field: 'item',
          filter: 'agTextColumnFilter',
          filterParams: {
            defaultOption: 'equals'
          },
          minWidth: 180
        },
        {
          headerName: 'Context Type',
          field: 'context_type',
          filter: 'agSetColumnFilter',
          minWidth: 180
        },
        {
          headerName: 'Context',
          field: 'context',
          filter: 'agTextColumnFilter',
          tooltipField: 'context',
          minWidth: 280,
          flex: 2
        },
        {
          headerName: 'Date',
          field: 'date',
          sort: 'desc',
          filter: 'agDateColumnFilter',
          minWidth: 170,
          cellRenderer: params => this.formatDateDisplay(params.value),
          tooltipValueGetter: params => {
            if (!params.value) return ''
            const m = moment(params.value)
            return `Local: ${m.local().format('YYYY-MM-DD HH:mm:ss')}`
          }
        }
      ]
    },
    getContextMenuItems() {
      return [
        'copy',
        'copyWithHeaders',
        'separator',
        'export'
      ]
    }
  }
}
</script>
